import { useEffect, useState } from "react";
import { LayoutGrid, Megaphone, FileText, CalendarClock, LogOut } from "lucide-react";
import { useAdminAuth } from "../lib/adminAuth";
import { adminApi } from "../lib/adminApi";
import { Logo } from "../components/Logo";
import { ProjectsPanel } from "../components/admin/ProjectsPanel";
import { AnnouncementPanel } from "../components/admin/AnnouncementPanel";
import { SubmissionsPanel } from "../components/admin/SubmissionsPanel";

const TABS = [
  { id: "projects", label: "Réalisations", icon: LayoutGrid },
  { id: "announcement", label: "Annonce", icon: Megaphone },
  { id: "devis", label: "Devis", icon: FileText },
  { id: "rdv", label: "Rendez-vous", icon: CalendarClock },
];

const countNew = (list) => (Array.isArray(list) ? list.filter((r) => r.status !== "traite").length : 0);

export default function Admin() {
  const { logout } = useAdminAuth();
  const [tab, setTab] = useState("projects");
  const [counts, setCounts] = useState({ devis: 0, rdv: 0 });

  const refreshCounts = async () => {
    try {
      const [d, r] = await Promise.all([adminApi.listDevis(), adminApi.listRdv()]);
      setCounts({ devis: countNew(d), rdv: countNew(r) });
    } catch (e) {
      /* ignore */
    }
  };

  // Refresh counts on mount and whenever the active tab changes (cheap, keeps badges live)
  useEffect(() => {
    refreshCounts();
  }, [tab]);

  const badgeFor = (id) => (id === "devis" ? counts.devis : id === "rdv" ? counts.rdv : 0);

  return (
    <div className="grain bg-ink text-white min-h-screen flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 lg:min-h-screen flex flex-col">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <Logo className="h-[30px]" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-accent-yellow">Admin</span>
        </div>
        <nav className="flex lg:flex-col gap-1 p-3 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => {
            const badge = badgeFor(id);
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm whitespace-nowrap transition-colors duration-200 ${
                  tab === id ? "bg-accent-yellow/10 text-accent-yellow" : "text-white/70 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                <Icon size={17} /> {label}
                {badge > 0 && (
                  <span data-testid={`admin-tab-badge-${id}`} className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent-yellow text-ink text-[11px] font-bold">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="mt-auto m-3 hidden lg:flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/60 hover:text-red-400 hover:bg-white/[0.04] transition-colors"
        >
          <LogOut size={17} /> Se déconnecter
        </button>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-10 max-w-4xl w-full">
        <div className="flex justify-end lg:hidden mb-4">
          <button onClick={logout} data-testid="admin-logout-mobile" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-red-400 transition-colors">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>

        {/* Untreated submissions summary */}
        <div className="grid grid-cols-2 gap-4 mb-8" data-testid="admin-stats">
          <button
            onClick={() => setTab("devis")}
            data-testid="stat-devis"
            className="text-left rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-accent-yellow/40 transition-colors"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-white/45">Devis nouveaux</p>
            <p className="mt-2 text-3xl font-display font-medium text-accent-yellow">{counts.devis}</p>
          </button>
          <button
            onClick={() => setTab("rdv")}
            data-testid="stat-rdv"
            className="text-left rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-accent-yellow/40 transition-colors"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-white/45">Rendez-vous nouveaux</p>
            <p className="mt-2 text-3xl font-display font-medium text-accent-yellow">{counts.rdv}</p>
          </button>
        </div>

        {tab === "projects" && <ProjectsPanel />}
        {tab === "announcement" && <AnnouncementPanel />}
        {tab === "devis" && <SubmissionsPanel type="devis" onChange={refreshCounts} />}
        {tab === "rdv" && <SubmissionsPanel type="rdv" onChange={refreshCounts} />}
      </main>
    </div>
  );
}
